import React, { SetStateAction, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import ProfileInfo from './components/ProfileInfo';
import ProfileForm from './components/ProfileForm';
import { InfluencerService } from '../../service';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../utils/toasters';
import { IProfileData } from '../profile-view';
import { useParams } from 'react-router-dom';
import { IParams } from '../../../../model/shared/IParams';
import { useTranslation } from 'react-i18next';
import { useUnsavedChangesPrompt } from '../../../../shared/hooks/usePrompt';
import { ICampaignCategories } from '../../../../model/campaign/CampaignCategory';
import { actions } from '../../../../store/authentication/authRedux';
import { useDispatch } from 'react-redux';

const InfluencerProfileEdit = () => {
    const [profileData, setProfileData] = useState<IProfileData>();
    const [updatePlatforms, setUpdatePlatforms] = useState(false);
    const { id } = useParams<keyof IParams>();
    const [initialAboutText, setInitialAboutText] = useState('');
    const [filesScreenshots, setFilesScreenshots] = useState<Blob[] | MediaSource[]>();
    const [files, setFiles] = useState<Blob[] | MediaSource[] | undefined>(undefined);

    const { t } = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        if (id) {
            InfluencerService.getInfluencerProfile(id as unknown as string)
                .then(response => {
                    setProfileData(response);
                    setInitialAboutText(response.user.about);
                })
                .catch(error => ErrorToast(error));
        }
    }, [id, updatePlatforms]);

    const onSubmit = async () => {
        const formData = new FormData();
        let successFlag = false;
        if (!profileData) return;
        if (profileData?.user?.categoriesArray?.length < 3 || profileData?.user?.categoriesArray?.length > 5) {
            return WarningToast(t('general.toasts.warnSelectCategory'));
        }
        if (files?.length && id) {
            for (const item of files) {
                formData.append('files', item as string | Blob);
            }
            await InfluencerService.uploadHighlighted(id, formData)
                .then(() => {
                    successFlag = true;
                    setUpdatePlatforms(prev => !prev);
                })
                .catch(error => ErrorToast(error));
        }

        await InfluencerService.updateInfluencerProfile(id ?? '', {
            ...profileData?.user,
            userCategories: profileData?.user?.categoriesArray as unknown as ICampaignCategories[],
        })
            .then(response => {
                if (response) {
                    dispatch(actions.updateComletionInfo(response?.completionInfo));
                    setProfileData(response);
                    setFiles([]);
                    successFlag = true;
                }
            })
            .catch(error => ErrorToast(error));

        const formDataScreenshots = new FormData();

        if (filesScreenshots?.length && id) {
            for (const item of filesScreenshots) {
                formDataScreenshots.append('files', item as string | Blob);
            }
            await InfluencerService.uploadKpiScreenshots(id, formDataScreenshots)
                .then(response => {
                    successFlag = true;
                    setUpdatePlatforms(prev => !prev);
                    // setProfileData(response);
                    setFilesScreenshots([]);
                    setFiles([]);
                })
                .catch(error => ErrorToast(error));
        }

        if (successFlag) {
            SuccessToast(t('influencer.profile.successUpdate'));
        }
    };

    useUnsavedChangesPrompt(t('influencer.profile.unsaved_changes'), initialAboutText !== profileData?.user?.about);

    return (
        <Row className="mb-5 pb-5 mb-md-0 pb-md-0 ">
            <Col lg={3} className="profile-info-sticky">
                <ProfileInfo
                    onSubmit={onSubmit}
                    defaultState={profileData?.user}
                    setUpdatePlatforms={setUpdatePlatforms}
                    isEdit
                />
            </Col>
            <Col lg={9}>
                <ProfileForm
                    userState={profileData as IProfileData}
                    setState={setProfileData as React.Dispatch<SetStateAction<IProfileData>>}
                    setUpdatePlatforms={setUpdatePlatforms}
                    setFilesScreenshots={setFilesScreenshots}
                    filesScreenshots={filesScreenshots}
                    handleUploadFiles={onSubmit}
                    files={files}
                    setFiles={setFiles}
                />
            </Col>
        </Row>
    );
};

export default InfluencerProfileEdit;
